// Copyright 2026 Zibby Lab. Apache-2.0.
//
// AgentHoneycomb — the deployed-agent avatar cluster shown next to a project's
// name: an Apple-Watch style HONEYCOMB of round agent icons, packed into rows
// that nest into each other's valleys, with the icons deliberately NOT all the
// same size so the cluster reads as an organic flower rather than a grid.
//
// Self-host dashboard parity (selfhosted/ui/index.html, projectsPage +
// .proj-agents). The two UIs share ZERO code — different repos, different
// paradigms, no build in common — so this is the second implementation of ONE
// design, and every number below is duplicated there on purpose. Change one,
// change the other.

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import LetterAvatar from '../LetterAvatar/LetterAvatar';
import './AgentHoneycomb.css';

// Icon diameters. Two sizes, mixed within the cluster — a honeycomb of
// identical circles looks mechanical; alternating breaks the grid up.
const BIG = 13;
const SMALL = 10;
// Centre-to-centre spacing.
const COL = 12; // horizontally, within a row
const ROW = 11; // vertically, between rows

/**
 * One row of the cluster: `n` icons centred on x=0 at height `y`, taking sizes
 * from `sizes` (cycled), shifted `dx` columns sideways. The shift is what makes
 * two EQUAL-width neighbouring rows nest instead of forming a square grid —
 * rows of different widths already nest for free, because both are centred.
 */
const row = (n, y, sizes, dx = 0) =>
  Array.from({ length: n }, (_, i) => ({
    x: (i - (n - 1) / 2 + dx) * COL,
    y,
    size: sizes[i % sizes.length],
  }));

// Layout per agent count. Up to FOUR agents stay in two rows; FIVE and up go to
// THREE, so the cluster grows in density rather than width and never turns into
// a long horizontal strip next to the project name.
const LAYOUTS = [
  [],                                                                   // 0
  row(1, 0, [BIG]),                                                     // 1
  row(2, 0, [BIG, SMALL]),                                              // 2
  [...row(2, -5.5, [BIG, SMALL]), ...row(1, 5.5, [BIG])],               // 3
  [...row(2, -5.5, [BIG, SMALL]), ...row(2, 5.5, [SMALL, BIG], 0.5)],   // 4
  [...row(1, -ROW, [SMALL]), ...row(3, 0, [BIG, SMALL, BIG]), ...row(1, ROW, [SMALL])],          // 5
  [...row(2, -ROW, [SMALL, BIG]), ...row(3, 0, [BIG, SMALL, BIG]), ...row(1, ROW, [SMALL])],     // 6
  [...row(2, -ROW, [SMALL, BIG]), ...row(3, 0, [BIG, SMALL, BIG]), ...row(2, ROW, [BIG, SMALL])],// 7
  [...row(3, -ROW, [SMALL, BIG, SMALL]), ...row(3, 0, [BIG, SMALL, BIG], 0.5), ...row(2, ROW, [SMALL, BIG])],       // 8
  [...row(3, -ROW, [SMALL, BIG, SMALL]), ...row(3, 0, [BIG, SMALL, BIG], 0.5), ...row(3, ROW, [SMALL, BIG, SMALL])],// 9
];
const MAX = LAYOUTS.length - 1;

// One cell: the agent's marketplace logo, falling back to the shared
// LetterAvatar when it has no icon (CLI-deployed / custom agent) or the image
// 404s — never a broken-image glyph. Same pattern as the Pipelines list icons.
// Both fill the cell, so the cell's inline size is the ONE place a diameter is
// set.
const HexIcon = ({ iconUrl, label }) => {
  const [failed, setFailed] = useState(false);
  if (!iconUrl || failed) {
    return <LetterAvatar name={label} className="agent-honeycomb-tile" title={label} />;
  }
  return (
    <img
      src={iconUrl}
      onError={() => setFailed(true)}
      className="agent-honeycomb-img"
      alt=""
      title={label}
      loading="lazy"
    />
  );
};

HexIcon.propTypes = { iconUrl: PropTypes.string, label: PropTypes.string };

/**
 * @param {Array<{key?:string, iconUrl?:string, label?:string}>} agents
 *        Deployed agents, in list order. Beyond the 9th is dropped (no "+N"
 *        pill — the cluster is a glanceable signal, not a census).
 */
const AgentHoneycomb = ({ agents = [] }) => {
  if (!agents.length) return null;
  const shown = agents.slice(0, MAX);
  const layout = LAYOUTS[shown.length];
  return (
    <span
      className="agent-honeycomb"
      aria-label={`${agents.length} deployed agent${agents.length === 1 ? '' : 's'}`}
    >
      {shown.map((a, i) => {
        const cell = layout[i];
        return (
          <span
            key={a.key || a.label || i}
            className="agent-honeycomb-cell"
            style={{
              left: `calc(50% + ${cell.x.toFixed(1)}px)`,
              top: `calc(50% + ${cell.y.toFixed(1)}px)`,
              width: `${cell.size}px`,
              height: `${cell.size}px`,
              // The letter fallback scales with its circle — a 13px glyph in a
              // 10px dot would just be a smudge.
              fontSize: `${Math.round(cell.size * 0.5)}px`,
            }}
          >
            <HexIcon iconUrl={a.iconUrl} label={a.label} />
          </span>
        );
      })}
    </span>
  );
};

AgentHoneycomb.propTypes = {
  agents: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    iconUrl: PropTypes.string,
    label: PropTypes.string,
  })),
};

export default AgentHoneycomb;
